// <infinote-box-builder>: 3-step box builder (Box -> Items -> Review) with live fit feedback,
// size tabs, order summary, filters, sticky tray with boxes stepper, and add to cart.
// Config comes from data attributes (mock JSON URL) or an inline JSON script. Fit logic lives in infinote-fit.js.
// All wording comes from window.infinoteBox.copy (theme locale group "infinote_box"); nothing is hard-coded here.
// Typography and colour come from theme tokens (see infinote-cart-boxes.css, :root block).

const STEPS = ['step_box', 'step_items', 'step_review'];
const MAX_BOXES = 9999;
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ESC[c]);
// tx: plain text with {placeholders} filled. t: the same, HTML-escaped (for markup).
const tx = (key, vars) => {
  const c = (window.infinoteBox && window.infinoteBox.copy) || {};
  return String(c[key] == null ? key : c[key]).replace(/\{(\w+)\}/g, (m, k) => (vars && k in vars ? vars[k] : m));
};
const t = (key, vars) => {
  const c = (window.infinoteBox && window.infinoteBox.copy) || {};
  const safe = {};
  Object.keys(vars || {}).forEach((k) => { safe[k] = esc(vars[k]); });
  return esc(String(c[key] == null ? key : c[key])).replace(/\{(\w+)\}/g, (m, k) => (k in safe ? safe[k] : m));
};

class InfinoteBoxBuilder extends HTMLElement {
  async connectedCallback() {
    this.fit = await import(this.dataset.fitUrl);
    this.data = this.dataset.mockUrl
      ? await (await fetch(this.dataset.mockUrl)).json()
      : JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    // Liquid emits a trailing empty object to keep the JSON valid; drop it and anything without usable dimensions.
    this.data.items = this.data.items.filter((i) => i.dims && i.dims.every((d) => d > 0));
    this.data.boxes = this.data.boxes.filter((b) => b.inner.every((d) => d > 0));
    // Shopify ids are numbers; DOM data attributes are strings. Normalise once.
    this.data.items.forEach((i) => { i.id = String(i.id); });
    this.data.boxes.forEach((b) => { b.id = String(b.id); });
    this.data.custom.id = String(this.data.custom.id);
    this.allowance = Number(this.dataset.allowance) || this.data.allowance || 1.3;
    // Same look as the theme's prices ("Rs. 1,249.00") so the builder and the cart read alike.
    const cur = this.data.currency || 'INR';
    this.money = { format: (v) => (cur === 'INR'
      ? 'Rs. ' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(v)) };
    this.state = this.blank();
    this.filters = { search: '', type: '', sort: '' };
    this.root = this.querySelector('[data-ibb-root]');
    this.root.addEventListener('click', (e) => this.onClick(e));
    this.root.addEventListener('input', (e) => this.onInput(e));
    this.root.addEventListener('change', (e) => { if (e.target.matches('[data-ibb-boxes]')) this.render(); });
    await this.restore();
    this.render();
  }

  blank() { return { step: 1, boxId: null, qty: {}, boxCount: 1, note: { to: '', from: '', message: '' } }; }

  // ---------- persistence, edit and share ----------
  get storeKey() { return 'infinote_box_state_v1'; }
  persist() {
    try {
      if (this.editing) return;
      localStorage.setItem(this.storeKey, JSON.stringify(this.state));
    } catch (e) { /* storage unavailable */ }
  }
  clearStored() { try { localStorage.removeItem(this.storeKey); } catch (e) { /* ignore */ } }

  // Accept only ids that exist in the current data and are still available.
  sanitise(st) {
    const ids = new Set(this.data.items.map((i) => i.id));
    const boxes = new Set([...this.data.boxes.map((b) => b.id), this.data.custom.id]);
    const unavailable = new Set(this.data.boxes.filter((b) => b.available === false).map((b) => b.id));
    const soldItems = new Set(this.data.items.filter((i) => i.available === false).map((i) => i.id));
    const out = this.blank();
    if (st.boxId && boxes.has(String(st.boxId)) && !unavailable.has(String(st.boxId))) out.boxId = String(st.boxId);
    Object.entries(st.qty || {}).forEach(([k, v]) => { if (ids.has(String(k)) && !soldItems.has(String(k)) && v > 0) out.qty[String(k)] = Math.min(999, v | 0); });
    out.boxCount = Math.min(MAX_BOXES, Math.max(1, st.boxCount | 0 || 1));
    out.note = { to: String(st.note?.to || '').slice(0, 60), from: String(st.note?.from || '').slice(0, 60), message: String(st.note?.message || '').slice(0, 160) };
    out.step = out.boxId ? Math.min(Math.max(st.step | 0 || 2, 2), 3) : 1;
    if (out.step === 3 && !Object.keys(out.qty).length) out.step = 2;
    return out;
  }

  async restore() {
    const params = new URLSearchParams(location.search);
    try {
      if (params.get('edit')) { await this.loadFromCart(params.get('edit')); return; }
      if (params.get('share')) {
        const j = JSON.parse(decodeURIComponent(escape(atob(params.get('share').replace(/-/g, '+').replace(/_/g, '/')))));
        this.state = this.sanitise({ boxId: j.b, qty: j.q, boxCount: j.n, step: 2 });
        return;
      }
      const saved = localStorage.getItem(this.storeKey);
      if (saved) this.state = this.sanitise(JSON.parse(saved));
    } catch (e) { /* ignore bad saved or shared state */ }
  }

  async loadFromCart(boxId) {
    const cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
    const lines = cart.items.filter((i) => i.properties && i.properties._box_id === boxId);
    const boxLine = lines.find((i) => i.properties._box_role === 'box');
    if (!boxLine) return;
    const c = this.data.custom;
    const custom = boxLine.variant_id === c.variantId || c.tiers.some((x) => x.variantId === boxLine.variant_id);
    const box = this.data.boxes.find((b) => b.variantId === boxLine.variant_id);
    const qty = {};
    lines.filter((i) => i.properties._box_role === 'item').forEach((l) => {
      const it = this.data.items.find((x) => x.variantId === l.variant_id);
      if (it) qty[it.id] = Math.max(1, Math.round(l.quantity / boxLine.quantity));
    });
    const p = boxLine.properties;
    this.state = this.sanitise({ boxId: custom ? c.id : box && box.id, qty, boxCount: boxLine.quantity, step: 2,
      note: { to: p['Gift to'], from: p['Gift from'], message: p['Gift message'] } });
    this.editing = { id: boxId, keys: lines.map((l) => l.key) };
  }

  shareUrl() {
    const s = this.state;
    const json = JSON.stringify({ b: s.boxId, q: s.qty, n: s.boxCount });
    const b64 = btoa(unescape(encodeURIComponent(json))).replace(/\+/g, '-').replace(/\//g, '_');
    return location.origin + location.pathname + '?share=' + b64;
  }

  // ---------- derived state ----------
  get stockBoxes() { return this.fit.sortBoxes(this.data.boxes); }
  get lines() { return this.data.items.map((item) => ({ item, qty: this.state.qty[item.id] || 0 })); }
  get activeLines() { return this.lines.filter((l) => l.qty > 0); }
  get itemCount() { return this.activeLines.reduce((n, l) => n + l.qty, 0); }
  get isCustom() { return this.state.boxId === this.data.custom.id; }
  get box() { return this.data.boxes.find((b) => b.id === this.state.boxId) || null; }
  get boxTitle() { return this.isCustom ? this.data.custom.title : this.box ? this.box.title : ''; }

  status() {
    const { fit, allowance } = this;
    if (!this.state.boxId) return { kind: 'choose' };
    if (this.isCustom) return this.itemCount ? { kind: 'custom', dims: fit.requiredCustomDims(this.lines, allowance) } : { kind: 'empty' };
    if (!this.itemCount) return { kind: 'empty' };
    const r = fit.contentsFit(this.lines, this.box, allowance);
    if (r.fits) return { kind: 'fits', ratio: r.ratio };
    const next = fit.upgradeBox(this.lines, this.data.boxes.filter((b) => b.available !== false), this.box, allowance);
    return next ? { kind: 'upgrade', next, ratio: r.ratio } : { kind: 'needs-custom', ratio: r.ratio };
  }

  price() {
    if (!this.state.boxId) return null;
    const n = this.state.boxCount;
    const boxPrice = this.isCustom ? this.fit.customBoxPrice(this.data.custom.tiers, n) : this.box.price;
    const items = this.lines.reduce((s, l) => s + l.item.price * l.qty, 0);
    return { boxPrice, items, perBox: boxPrice + items, total: (boxPrice + items) * n };
  }

  canComplete() { return this.itemCount > 0 && ['fits', 'custom'].includes(this.status().kind) && !(this.box && this.box.available === false); }

  // ---------- events ----------
  onClick(e) {
    const el = e.target.closest('[data-act]');
    if (!el || el.disabled) return;
    const { act, id } = el.dataset;
    const s = this.state;
    if (el.getAttribute('aria-disabled') === 'true') { this.flash(el.dataset.why || ''); return; }
    switch (act) {
      case 'pick-box': s.boxId = id; s.step = 2; break;
      case 'tab-box': s.boxId = id; break;
      case 'add': s.qty[id] = (s.qty[id] || 0) + 1; break;
      case 'sub': s.qty[id] = Math.max(0, (s.qty[id] || 0) - 1); break;
      case 'remove': delete s.qty[id]; break;
      case 'boxes-add': s.boxCount = Math.min(MAX_BOXES, s.boxCount + 1); break;
      case 'boxes-sub': s.boxCount = Math.max(1, s.boxCount - 1); break;
      case 'upgrade': s.boxId = this.status().next.id; break;
      case 'use-custom': s.boxId = this.data.custom.id; break;
      case 'goto': if (Number(id) < s.step || (Number(id) === 2 && s.boxId)) s.step = Number(id); break;
      case 'complete': if (this.canComplete()) s.step = 3; break;
      case 'back': s.step = 2; break;
      case 'reset':
        if (!this.confirmReset) { this.confirmReset = true; clearTimeout(this.resetTimer); this.resetTimer = setTimeout(() => { this.confirmReset = false; this.render(); }, 4000); break; }
        this.confirmReset = false; this.state = this.blank(); this.error = null; this.editing = null; this.clearStored(); break;
      case 'share': return this.share();
      case 'checkout': return this.addToCart();
      case 'close-payload': this.payload = null; break;
      default: return;
    }
    if (['pick-box', 'complete', 'back', 'goto'].includes(act)) this.scrollIntoView({ block: 'start' });
    this.persist();
    this.render();
    this.refocus(el);
  }

  // render() rebuilds the DOM, so put keyboard focus back on the equivalent control.
  refocus(el) {
    if (['pick-box', 'complete', 'back', 'goto', 'checkout', 'close-payload'].includes(el.dataset.act)) return;
    const sel = `[data-act="${el.dataset.act}"]` + (el.dataset.id ? `[data-id="${el.dataset.id}"]` : '');
    const next = this.root.querySelector(sel) || this.root.querySelector(`[data-act="${el.dataset.act === 'sub' ? 'add' : 'sub'}"][data-id="${el.dataset.id}"]`);
    if (next && !next.disabled) next.focus({ preventScroll: true });
  }

  flash(msg) {
    if (!msg) return;
    this.notice = msg;
    this.render();
    clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(() => { this.notice = null; this.render(); }, 4000);
  }

  onInput(e) {
    const el = e.target;
    if (el.matches('[data-ibb-boxes]')) {
      const n = parseInt(el.value, 10);
      if (!(n >= 1)) return; // let the field be empty while typing; 'change' normalises it
      this.state.boxCount = Math.min(MAX_BOXES, n);
    }
    else if (el.matches('[data-ibb-search]')) this.filters.search = el.value;
    else if (el.matches('[data-ibb-type]')) this.filters.type = el.value;
    else if (el.matches('[data-ibb-sort]')) this.filters.sort = el.value;
    else if (el.matches('[data-ibb-note]')) this.state.note[el.dataset.ibbNote] = el.value;
    else return;
    this.persist();
    this.render(el.dataset.focus);
  }

  async share() {
    const url = this.shareUrl();
    try { await navigator.clipboard.writeText(url); this.shared = tx('share_copied'); } catch (e) { this.shared = url; }
    this.render();
    setTimeout(() => { this.shared = null; this.render(); }, 6000);
  }

  noteProps() {
    const n = this.state.note;
    const p = {};
    if (n.to.trim()) p['Gift to'] = n.to.trim();
    if (n.from.trim()) p['Gift from'] = n.from.trim();
    if (n.message.trim()) p['Gift message'] = n.message.trim();
    return p;
  }

  async addToCart() {
    if (!this.canComplete() || this.busy) return;
    const st = this.status();
    const items = this.fit.buildCartItems({
      box: { title: this.boxTitle },
      boxVariantId: this.isCustom ? this.customVariantId() : this.box.variantId,
      lines: this.lines,
      boxCount: this.state.boxCount,
      boxId: 'box-' + Date.now().toString(36),
      customDims: this.isCustom ? st.dims : null,
      boxProps: this.noteProps(),
    });
    if (this.dataset.mockUrl) { this.payload = items; this.render(); return; } // mock mode: no cart write
    this.error = null;
    this.busy = true;
    this.render();
    const root = window.Shopify.routes.root;
    const post = (path, body) => fetch(root + path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    try {
      const res = await post('cart/add.js', { items });
      if (!res.ok) { this.error = (await res.json()).description || tx('err_add'); return; }
      // Only after the new box is safely in the cart do we remove the one being edited.
      if (this.editing) for (const key of this.editing.keys) await post('cart/change.js', { id: key, quantity: 0 });
      this.clearStored();
      window.location.href = root + 'cart';
    } catch (e) {
      this.error = tx('err_network');
    } finally {
      this.busy = false;
      if (!/\/cart$/.test(location.pathname)) this.render();
    }
  }

  // Variant for the custom box tier that matches the number of boxes (real data); falls back to the product variant.
  customVariantId() {
    const c = this.data.custom;
    const tier = [...c.tiers].sort((a, b) => a.min - b.min).filter((x) => this.state.boxCount >= x.min).pop();
    return (tier && tier.variantId) || c.variantId;
  }

  // ---------- rendering ----------
  fmtDims(d) { return d.join(' × ') + ' mm'; }
  img(o, cls) {
    return o.image ? `<img class="${cls}" src="${esc(o.image)}" alt="" loading="lazy" width="400" height="400">`
      : `<span class="${cls} ibb__ph" aria-hidden="true">${esc((o.title || '?').trim().charAt(0))}</span>`;
  }

  render(focusKey) {
    const s = this.state;
    const body = s.step === 1 ? this.viewBox() : s.step === 2 ? this.viewItems() : this.viewReview();
    this.root.innerHTML = this.viewProgress() + body + (s.step === 2 ? this.viewTray() : '') + this.viewPayload();
    if (focusKey) {
      const el = this.root.querySelector(`[data-focus="${focusKey}"]`);
      if (el) { el.focus(); if (el.setSelectionRange && el.type !== 'number') el.setSelectionRange(el.value.length, el.value.length); }
    }
  }

  viewProgress() {
    const cur = this.state.step;
    const items = STEPS.map((key, i) => {
      const n = i + 1;
      const done = n < cur;
      const nav = done || (n === 2 && this.state.boxId) ? ` data-act="goto" data-id="${n}"` : '';
      const dot = n === cur ? `<span class="ibb__dot is-current">${t(key)}</span>` : `<span class="ibb__dot${done ? ' is-done' : ''}">${done ? '✓' : n}</span>`;
      return `<li class="ibb__stepitem"><button type="button" class="ibb__stepbtn"${nav} ${nav ? '' : 'tabindex="-1"'} aria-label="${t('step_aria', { n, name: tx(key) })}"${n === cur ? ' aria-current="step"' : ''}>${dot}</button><span class="ibb__steplabel">${t('step_label', { n })}</span></li>`;
    }).join('<li class="ibb__rule" aria-hidden="true"></li>');
    return `<ol class="ibb__progress">${items}</ol>`;
  }

  head(n, titleKey, subKey) {
    return `<header class="ibb__head"><p class="ibb__kicker">${t('step_of', { n })}</p><h2 class="ibb__title">${t(titleKey)}</h2>${subKey ? `<p class="ibb__sub">${t(subKey)}</p>` : ''}</header>`;
  }

  // Step 1
  viewBox() {
    const card = (b, custom) => {
      const price = custom ? tx('price_from', { price: this.money.format(Math.min(...this.data.custom.tiers.map((x) => x.price))) }) : this.money.format(b.price);
      const detail = custom ? tx('custom_detail', { moq: b.moq }) : this.fmtDims(b.inner);
      const off = b.available === false;
      return `<button type="button" class="ibb__boxcard${off ? ' is-off' : ''}" data-act="pick-box" data-id="${b.id}" ${off ? 'disabled' : ''}>${this.img(b, 'ibb__boxcard-img')}
        <span class="ibb__boxcard-title">${esc(b.title)}</span><span class="ibb__muted">${esc(detail)}</span>
        <span class="ibb__boxcard-foot"><span class="ibb__boxcard-price">${off ? t('sold_out') : esc(price)}</span>${off ? '' : `<span class="ibb__boxcard-cta">${t('box_select')}</span>`}</span></button>`;
    };
    return `${this.head(1, 'choose_title', 'choose_sub')}
      <div class="ibb__boxcards">${this.stockBoxes.map((b) => card(b)).join('')}${card(this.data.custom, true)}</div>`;
  }

  // Step 2
  viewItems() {
    const st = this.status();
    const pr = this.price();
    const tab = (b, custom) => {
      const sel = this.state.boxId === b.id;
      const sold = b.available === false;
      const fits = !sold && (custom || !this.itemCount || this.fit.contentsFit(this.lines, b, this.allowance).fits);
      const why = sold ? tx('tab_sold', { box: b.title }) : tx('tab_small', { box: b.title });
      return `<button type="button" class="ibb__tab${sel ? ' is-selected' : ''}${fits ? '' : ' is-off'}" data-act="tab-box" data-id="${b.id}"
        aria-pressed="${sel}" ${fits || sel ? '' : `aria-disabled="true" data-why="${esc(why)}"`}>${esc(b.title)}</button>`;
    };
    const tabs = this.stockBoxes.map((b) => tab(b)).join('') + tab(this.data.custom, true);
    const lines = this.activeLines.map((l) => this.lineRow(l, false)).join('');
    const active = this.activeLines;
    const thumbs = active.map((l) => `<span class="ibb__thumb-lg-wrap">${this.img(l.item, 'ibb__thumb-lg')}${l.qty > 1 ? `<span class="ibb__badge">×${l.qty}</span>` : ''}</span>`).join('');
    const ratio = st.ratio != null ? Math.min(st.ratio, 1) : null;
    const pct = ratio != null ? Math.round(ratio * 100) : null;
    const boxObj = this.isCustom ? this.data.custom : this.box;
    const preview = `<div class="ibb__preview">
      <div class="ibb__preview-box">${boxObj ? this.img(boxObj, 'ibb__preview-img') : ''}
        <div class="ibb__preview-info"><p class="ibb__preview-name">${esc(this.boxTitle)}</p>
          ${this.box ? `<p class="ibb__muted">${t('preview_inner', { dims: this.fmtDims(this.box.inner) })}</p>` : ''}
          ${pct != null ? `<div class="ibb__meter${st.kind === 'fits' ? '' : ' is-over'}" role="img" aria-label="${st.kind === 'fits' ? t('preview_used', { pct }) : t('preview_over')}"><span style="width:${st.kind === 'fits' ? pct : 100}%"></span></div><p class="ibb__muted">${st.kind === 'fits' ? t('preview_used', { pct }) : t('preview_over')}</p>` : ''}</div></div>
      ${active.length ? `<div class="ibb__thumbs">${thumbs}</div>` : `<p class="ibb__empty">${t('preview_empty')}</p>`}</div>`;
    return `${this.head(2, 'items_title', 'items_sub')}
      <div class="ibb__wrap">
        <div class="ibb__tabs" role="group" aria-label="${t('box_size')}">${tabs}</div>
        ${this.statusBar(st)}
        ${this.notice ? `<p class="ibb__notice" role="status">${esc(this.notice)}</p>` : ''}
        <div class="ibb__panel">
          ${preview}
          <aside class="ibb__summary" aria-label="${t('summary_title')}">
            <h3 class="ibb__summary-title">${t('summary_title')}</h3>
            ${this.editing ? `<p class="ibb__muted">${t('summary_editing')}</p>` : ''}
            <ul class="ibb__lines"><li class="ibb__line"><span>${esc(this.boxTitle)}</span><span class="ibb__price">${pr ? esc(this.money.format(pr.boxPrice)) : ''}</span></li>${lines}</ul>
            <p class="ibb__subtotal"><span>${t('subtotal')}</span><strong class="ibb__price">${pr ? esc(this.money.format(pr.perBox)) : ''}</strong></p>
            <div class="ibb__row"><button type="button" class="ibb__btn ibb__btn--line" data-act="share" ${this.itemCount && this.state.boxId ? '' : 'disabled'}>${t('share')}</button><button type="button" class="ibb__btn" data-act="complete" ${this.canComplete() ? '' : 'disabled'}>${t('complete')}</button></div>
            ${this.shared ? `<p class="ibb__muted" role="status">${esc(this.shared)}</p>` : ''}
            <button type="button" class="ibb__link" data-act="reset">${this.confirmReset ? t('reset_confirm') : t('reset')}</button>
          </aside>
        </div>
        ${this.viewFilters()}
        ${this.viewGrid()}
      </div>`;
  }

  // One priced line (summary and review). withThumb adds a small image.
  lineRow(l, withThumb) {
    const q = l.qty > 1 || withThumb ? ` × ${l.qty}` : '';
    return `<li class="ibb__line${withThumb ? ' ibb__line--thumb' : ''}">${withThumb ? this.img(l.item, 'ibb__line-img') : ''}<span class="ibb__line-name">${esc(l.item.title)}${q}</span><span class="ibb__price">${esc(this.money.format(l.item.price * l.qty))}</span></li>`;
  }

  statusBar(st) {
    const c = this.data.custom;
    const m = {
      empty: ['info', t('status_empty')],
      choose: ['info', t('status_choose')],
      fits: ['ok', t('status_fits', { pct: Math.round(Math.min(st.ratio || 0, 1) * 100) })],
      upgrade: ['warn', st.next ? t('status_upgrade', { box: this.boxTitle, next: st.next.title }) : ''],
      'needs-custom': ['warn', t('status_needs_custom')],
      custom: ['ok', st.dims ? t('status_custom', { dims: this.fmtDims(st.dims), moq: c.moq }) : ''],
    }[st.kind];
    const action = st.kind === 'upgrade' ? ['upgrade', t('action_upgrade', { next: st.next.title, price: this.money.format(st.next.price) })]
      : st.kind === 'needs-custom' ? ['use-custom', t('action_custom')] : null;
    return `<div class="ibb__status ibb__status--${m[0]}" role="status" aria-live="polite"><span>${m[1]}</span>${action ? `<button type="button" class="ibb__btn ibb__btn--line" data-act="${action[0]}">${action[1]}</button>` : ''}</div>`;
  }

  viewFilters() {
    const types = [...new Set(this.data.items.map((i) => i.type).filter(Boolean))].sort();
    const f = this.filters;
    const opt = (v, label, cur) => `<option value="${esc(v)}"${v === cur ? ' selected' : ''}>${esc(label)}</option>`;
    return `<div class="ibb__filters">
      ${types.length ? `<select data-ibb-type data-focus="type" aria-label="${t('filter_category')}">${opt('', tx('filter_all'), f.type)}${types.map((x) => opt(x, x, f.type)).join('')}</select>` : ''}
      <select data-ibb-sort data-focus="sort" aria-label="${t('filter_sort')}">${opt('', tx('filter_sort'), f.sort)}${opt('price-asc', tx('sort_price_asc'), f.sort)}${opt('price-desc', tx('sort_price_desc'), f.sort)}${opt('size-asc', tx('sort_size_asc'), f.sort)}${opt('size-desc', tx('sort_size_desc'), f.sort)}</select>
      <input type="search" data-ibb-search data-focus="search" placeholder="${t('search')}" value="${esc(f.search)}" aria-label="${t('search_label')}"></div>`;
  }

  viewGrid() {
    const f = this.filters;
    const q = f.search.trim().toLowerCase();
    const vol = (i) => i.dims[0] * i.dims[1] * i.dims[2];
    let items = this.data.items.filter((i) => (!q || i.title.toLowerCase().includes(q)) && (!f.type || i.type === f.type));
    const sorts = { 'price-asc': (a, b) => a.price - b.price, 'price-desc': (a, b) => b.price - a.price, 'size-asc': (a, b) => vol(a) - vol(b), 'size-desc': (a, b) => vol(b) - vol(a) };
    if (sorts[f.sort]) items = [...items].sort(sorts[f.sort]);
    if (!items.length) return `<p class="ibb__muted">${t('no_match')}</p>`;
    return `<ul class="ibb__grid">${items.map((i) => this.card(i)).join('')}</ul>`;
  }

  // The control sits under the photo (never over it) and is always visible.
  card(i) {
    const n = this.state.qty[i.id] || 0;
    const sold = i.available === false;
    const ctl = sold && !n ? `<span class="ibb__soldout">${t('sold_out')}</span>` : n
      ? `<div class="ibb__qty"><button type="button" class="ibb__step-btn" data-act="sub" data-id="${i.id}" aria-label="${t('remove_one', { title: i.title })}">−</button><span aria-live="polite">${t('in_box', { n: String(n).padStart(2, '0') })}</span><button type="button" class="ibb__step-btn" data-act="add" data-id="${i.id}" aria-label="${t('add_one', { title: i.title })}">+</button></div>`
      : `<button type="button" class="ibb__add" data-act="add" data-id="${i.id}" aria-label="${t('add_label', { title: i.title })}">${t('add')}</button>`;
    return `<li class="ibb__card${n ? ' is-in' : ''}${sold ? ' is-sold' : ''}"><div class="ibb__card-media">${this.img(i, 'ibb__card-img')}</div>
      <p class="ibb__card-title" title="${esc(i.title)}">${esc(i.title)}</p><p class="ibb__muted">${esc(this.fmtDims(i.dims))}</p><p class="ibb__price">${esc(this.money.format(i.price))}</p><div class="ibb__card-ctl">${ctl}</div></li>`;
  }

  boxesCount(label) {
    return `<label class="ibb__count"><span>${label}</span><button type="button" class="ibb__step-btn" data-act="boxes-sub" aria-label="${t('fewer_boxes')}">−</button><input data-ibb-boxes data-focus="boxes" type="number" min="1" max="${MAX_BOXES}" inputmode="numeric" value="${this.state.boxCount}"><button type="button" class="ibb__step-btn" data-act="boxes-add" aria-label="${t('more_boxes')}">+</button></label>`;
  }

  // Sticky tray (step 2)
  viewTray() {
    const pr = this.price();
    const thumbs = this.activeLines.map((l) => `<span class="ibb__tray-thumb">${this.img(l.item, 'ibb__thumb')}<button type="button" class="ibb__x" data-act="remove" data-id="${l.item.id}" aria-label="${t('remove_item', { title: l.item.title })}">×</button></span>`).join('');
    const ok = this.canComplete();
    const label = ok ? `${t('complete')} · ${esc(this.money.format(pr.total))}` : this.itemCount ? t('tray_blocked') : t('tray_empty');
    return `<div class="ibb__tray" role="region" aria-label="${t('tray_label')}">
      <div class="ibb__tray-thumbs">${thumbs}</div>
      <div class="ibb__tray-info"><span class="ibb__kicker">${t('tray_size', { box: this.boxTitle })}</span>${this.boxesCount(t('boxes'))}</div>
      <button type="button" class="ibb__btn" data-act="complete" ${ok ? '' : 'disabled'}>${label}</button></div>`;
  }

  // Step 3
  viewReview() {
    const pr = this.price();
    const lines = this.activeLines.map((l) => this.lineRow(l, true)).join('');
    const st = this.status();
    const boxObj = this.isCustom ? this.data.custom : this.box;
    return `${this.head(3, 'review_title', 'review_sub')}
      <div class="ibb__wrap ibb__review">
        ${this.statusBar(st)}
        <ul class="ibb__lines"><li class="ibb__line ibb__line--thumb">${boxObj ? this.img(boxObj, 'ibb__line-img') : ''}<span class="ibb__line-name"><strong>${esc(this.boxTitle)}</strong></span><span class="ibb__price">${pr ? esc(this.money.format(pr.boxPrice)) : ''}</span></li>${lines}</ul>
        <p class="ibb__subtotal"><span>${t('per_box')}</span><strong class="ibb__price">${pr ? esc(this.money.format(pr.perBox)) : ''}</strong></p>
        ${this.boxesCount(t('number_of_boxes'))}
        <p class="ibb__subtotal ibb__subtotal--total"><span>${t('total')}</span><strong class="ibb__price">${pr ? esc(this.money.format(pr.total)) : ''}</strong></p>
        <fieldset class="ibb__note"><legend>${t('note_legend')}</legend>
          <label>${t('note_to')}<input type="text" maxlength="60" data-ibb-note="to" data-focus="note-to" value="${esc(this.state.note.to)}"></label>
          <label>${t('note_from')}<input type="text" maxlength="60" data-ibb-note="from" data-focus="note-from" value="${esc(this.state.note.from)}"></label>
          <label>${t('note_message')}<textarea maxlength="160" rows="3" data-ibb-note="message" data-focus="note-message">${esc(this.state.note.message)}</textarea></label>
          <span class="ibb__muted">${t('note_remaining', { n: 160 - this.state.note.message.length })}</span>
        </fieldset>
        <p class="ibb__muted">${t('discount_note')}</p>
        ${this.error ? `<p class="ibb__error" role="alert">${esc(this.error)}</p>` : ''}
        <div class="ibb__row"><button type="button" class="ibb__btn ibb__btn--line" data-act="back">${t('back')}</button>
          <button type="button" class="ibb__btn" data-act="checkout" ${ok(this) ? '' : 'disabled'}>${this.busy ? t('adding') : t('add_to_cart')}</button></div>
      </div>`;
  }

  viewPayload() {
    return this.payload ? `<div class="ibb__payload" role="dialog" aria-label="Mock cart payload"><p><strong>Mock mode.</strong> Nothing was added to the cart. This is what would be sent:</p><pre>${esc(JSON.stringify(this.payload, null, 2))}</pre><button type="button" class="ibb__btn ibb__btn--line" data-act="close-payload">Close</button></div>` : '';
  }
}
const ok = (b) => b.canComplete() && !b.busy;

if (!customElements.get('infinote-box-builder')) customElements.define('infinote-box-builder', InfinoteBoxBuilder);
